import { useState, useRef, useCallback, useEffect } from 'react';
/// <reference path="../types/speech-recognition.d.ts" />

// ---------- Types ----------

type WakeWordError = 'unsupported' | 'mic_denied' | 'engine_failed' | null;

interface UseEveWakeWordOptions {
  onWake: () => void;
  enabled?: boolean;
}

interface UseEveWakeWordReturn {
  isListening: boolean;
  error: WakeWordError;
  start: () => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  restart: () => void;
}

// ---------- Constants ----------

const WAKE_PATTERNS = [
  /hey\s*eve/i,
  /hey\s*e[fv]/i,
  /a\s+eve/i,
  /hey\s*eave/i,
];

const COOLDOWN_MS = 3_000;
const MAX_CONSECUTIVE_ERRORS = 5;
const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const RECOVERY_DELAY_MS = 10_000;

// ---------- Helpers ----------

function matchesWake(text: string): boolean {
  const normalized = text.toLowerCase().trim();
  return WAKE_PATTERNS.some((p) => p.test(normalized));
}

// ---------- Hook ----------

export function useEveWakeWord({ onWake, enabled = true }: UseEveWakeWordOptions): UseEveWakeWordReturn {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<WakeWordError>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const pausedRef = useRef(false);
  const stoppedRef = useRef(true);
  const consecutiveErrorsRef = useRef(0);
  const backoffRef = useRef(MIN_BACKOFF_MS);
  const restartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownRef = useRef(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onWakeRef = useRef(onWake);

  // Keep callback ref current
  useEffect(() => {
    onWakeRef.current = onWake;
  }, [onWake]);

  // Cleanup helper
  const clearTimers = useCallback(() => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  const clearCooldown = useCallback(() => {
    cooldownRef.current = false;
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  }, []);

  const destroyRecognition = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.abort();
      } catch {
        // ignore
      }
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  // ----- Core start logic -----
  const startInternal = useCallback(() => {
    if (pausedRef.current || stoppedRef.current) return;

    // Feature detection
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('unsupported');
      return;
    }

    destroyRecognition();

    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    recognitionRef.current = rec;

    rec.onresult = (event: SpeechRecognitionEvent) => {
      // Valid recognition → reset backoff
      consecutiveErrorsRef.current = 0;
      backoffRef.current = MIN_BACKOFF_MS;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0]?.transcript ?? '';
        if (matchesWake(transcript) && !cooldownRef.current) {
          // Trigger cooldown
          cooldownRef.current = true;
          cooldownTimerRef.current = setTimeout(() => {
            cooldownRef.current = false;
          }, COOLDOWN_MS);

          onWakeRef.current();
          return;
        }
      }
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.warn('[EveWakeWord] SpeechRecognition error:', event.error);

      if (event.error === 'not-allowed') {
        setError('mic_denied');
        stoppedRef.current = true;
        setIsListening(false);
        return;
      }

      consecutiveErrorsRef.current++;
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        setError('engine_failed');
        setIsListening(false);
        // Schedule auto-recovery instead of permanent stop
        recoveryTimerRef.current = setTimeout(() => {
          console.log('[EveWakeWord] Auto-recovering after engine_failed');
          consecutiveErrorsRef.current = 0;
          backoffRef.current = MIN_BACKOFF_MS;
          setError(null);
          if (!stoppedRef.current) {
            startInternal();
          }
        }, RECOVERY_DELAY_MS);
        return;
      }
      // Let onend handle restart with backoff
    };

    rec.onend = () => {
      setIsListening(false);
      if (stoppedRef.current || pausedRef.current) return;

      // Schedule restart with backoff
      const delay = backoffRef.current;
      backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);

      restartTimerRef.current = setTimeout(() => {
        startInternal();
      }, delay);
    };

    try {
      rec.start();
      setIsListening(true);
      setError(null);
    } catch (err) {
      console.error('[EveWakeWord] Failed to start:', err);
      setError('engine_failed');
    }
  }, [destroyRecognition]);

  // ----- Public API -----

  const start = useCallback(() => {
    stoppedRef.current = false;
    pausedRef.current = false;
    consecutiveErrorsRef.current = 0;
    backoffRef.current = MIN_BACKOFF_MS;
    clearCooldown();
    setError(null);
    startInternal();
  }, [startInternal, clearCooldown]);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    clearTimers();
    clearCooldown();
    destroyRecognition();
  }, [clearTimers, clearCooldown, destroyRecognition]);

  const pause = useCallback(() => {
    pausedRef.current = true;
    clearTimers();
    destroyRecognition();
  }, [clearTimers, destroyRecognition]);

  const resume = useCallback(() => {
    if (!stoppedRef.current) {
      pausedRef.current = false;
      startInternal();
    }
  }, [startInternal]);

  // Dedicated restart: full stop + clear all state + fresh start
  const restart = useCallback(() => {
    stoppedRef.current = true;
    clearTimers();
    clearCooldown();
    destroyRecognition();
    // Small delay to ensure browser releases mic
    setTimeout(() => {
      stoppedRef.current = false;
      pausedRef.current = false;
      consecutiveErrorsRef.current = 0;
      backoffRef.current = MIN_BACKOFF_MS;
      setError(null);
      startInternal();
    }, 100);
  }, [clearTimers, clearCooldown, destroyRecognition, startInternal]);

  // Auto-start when enabled, cleanup on unmount
  useEffect(() => {
    if (enabled) {
      start();
    }
    return () => {
      stoppedRef.current = true;
      clearTimers();
      destroyRecognition();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { isListening, error, start, stop, pause, resume, restart };
}
